import * as React from 'react';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Divider from '@mui/material/Divider';
import AccountCircle from '@mui/icons-material/AccountCircle';
import LogoutIcon from '@mui/icons-material/Logout';
import HistoryIcon from '@mui/icons-material/History';
import Avatar from '@mui/material/Avatar';
import { Typography } from '@mui/material';
import { signOut, onAuthStateChanged } from 'firebase/auth';
import { auth } from '../firebase/firebase';
import { useNavigate } from 'react-router-dom';
import ListItemIcon from '@mui/material/ListItemIcon';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import ReceiptIcon from '@mui/icons-material/Receipt';

interface ProfileDropdownProps {
  userName?: string;
  userEmail?: string;
  onHistory?: () => void;
  onLogout?: () => void;
  sx?: object;
}

export default function ProfileDropdown({
  userName,
  userEmail,
  onHistory,
  onLogout,
  sx,
  ...props
}: ProfileDropdownProps) {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = React.useState(auth.currentUser);

  React.useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
    });
    return unsubscribe;
  }, []);

  // Use provided props or fallback to auth values
  const displayName = userName || currentUser?.displayName || "Unknown User";
  const email = userEmail || currentUser?.email || "unknown@example.com";

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const defaultLogout = async () => {
    await signOut(auth);
    navigate('/signin'); // Redirect to Sign In page after logout
  };

  const handleHistoryClick = () => {
    if (onHistory) {
      onHistory();
    } else {
      navigate('/history'); // Default to /history route
    }
    handleClose();
  };

  const handleLogoutClick = async () => {
    if (onLogout) {
      onLogout();
    } else {
      await defaultLogout();
    }
    handleClose();
  };

  const handleMenuItemClick = (path: string) => {
    navigate(path);
    handleClose();
  };

  return (
    <>
      <IconButton
        onClick={handleClick}
        sx={{
          position: 'fixed',
          top: '1.3rem',
          right: '8.7rem',
          zIndex: 2000,
          width: 40,
          height: 40,
          ...sx,
        }}
        {...props}
      >
        {currentUser?.photoURL ? (
          <Avatar alt={displayName} src={currentUser.photoURL} />
        ) : (
          <AccountCircle sx={{ fontSize: 32 }} />
        )}
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        id="profile-menu"
        open={open}
        onClose={handleClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        PaperProps={{
          elevation: 4,
          sx: {
            borderRadius: 2,
            mt: 1,
            minWidth: 220,
          },
        }}
      >
        <MenuItem disabled>
          <Typography variant="subtitle1">{displayName}</Typography>
        </MenuItem>
        <MenuItem disabled>
          <Typography variant="body2" color="text.secondary">
            {email}
          </Typography>
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => handleMenuItemClick('/analyse')}>
          <ListItemIcon>
            <AnalyticsIcon fontSize="small" />
          </ListItemIcon>
          New Cargo
        </MenuItem>
        <MenuItem onClick={() => handleMenuItemClick('/history')}>
          <ListItemIcon>
            <HistoryIcon fontSize="small" />
          </ListItemIcon>
          History
        </MenuItem>
        <MenuItem onClick={() => handleMenuItemClick('/billing-history')}>
          <ListItemIcon>
            <ReceiptIcon fontSize="small" />
          </ListItemIcon>
          Billing History
        </MenuItem>
        <MenuItem onClick={handleLogoutClick}>
          <LogoutIcon sx={{ mr: 1 }} />
          <Typography variant="inherit">Logout</Typography>
        </MenuItem>
      </Menu>
    </>
  );
}
